import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/api";
import { NOTEPAD } from "@/constants/testIds";
import { Notebook, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.trim().length < 4) {
      setError("Kata sandi minimal 4 karakter");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(password);
      } else {
        await register(password);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-screen flex items-center justify-center bg-[#0A0B10] relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-fuchsia-600/10 rounded-full blur-3xl" />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-[#12141D]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-indigo-500/10 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
              <Notebook className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-indigo-400 font-mono">Notepad+</div>
              <h1 className="text-2xl font-bold tracking-tight text-white">
                {mode === "login" ? "Masuk" : "Daftar"}
              </h1>
            </div>
          </div>

          <p className="text-sm text-[#8B949E] mb-6 leading-relaxed">
            {mode === "login"
              ? "Masukkan kata sandi Anda untuk melanjutkan catatan."
              : "Buat kata sandi unik. Ini menjadi kunci akses catatan Anda—tanpa email, tanpa nama pengguna."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#8B949E] mb-2 block uppercase tracking-wider">
                Kata Sandi
              </label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  data-testid={NOTEPAD.authPasswordInput}
                  className="bg-[#0A0B10] border-[#2A2F45] text-white h-12 pr-12 font-mono tracking-wide focus-visible:ring-indigo-500 focus-visible:ring-offset-0"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B949E] hover:text-white transition-colors"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid={NOTEPAD.authErrorMsg}
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 px-3 py-2 rounded-md"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid={NOTEPAD.authSubmitBtn}
              className="w-full h-12 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 transition-all hover:-translate-y-0.5"
            >
              {loading ? "Memproses..." : mode === "login" ? "Masuk" : "Daftar"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/5 text-center">
            <button
              type="button"
              data-testid={NOTEPAD.authToggleMode}
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-sm text-[#8B949E] hover:text-indigo-400 transition-colors"
            >
              {mode === "login" ? (
                <>Belum punya akun? <span className="text-indigo-400 font-medium">Daftar sekarang</span></>
              ) : (
                <>Sudah punya akun? <span className="text-indigo-400 font-medium">Masuk</span></>
              )}
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-[#4B5563] mt-6 font-mono">
          simpan • sorot • lanjutkan tepat di titik terakhir
        </p>
      </motion.div>
    </div>
  );
}
